/**
 * Preparación de la ejecución (doc. 02 §4): valida, compila el layout y congela una configuración
 * inmutable con huellas de layout, proceso, parámetros y demanda. Dos ejecuciones con las mismas
 * huellas y semilla dan resultados idénticos.
 */

import { hashTexto } from '../aleatoriedad/generador';
import type { Catalogo, Espacio } from '../dominio/espacio';
import {
  REPLICAS_INICIALES,
  VERSION_MOTOR,
  type APieCompilado,
  type ConfiguracionDeEjecucion,
  type EstacionCompilada,
  type PersonaCompilada,
} from '../dominio/ejecucion';
import type { Observacion } from '../dominio/observacion';
import { LETRA_ROL, ROLES } from '../dominio/proceso';
import { SEGUNDOS_POR_MINUTO, horaASegundos, instante } from '../dominio/tiempo';
import { distanciasAPie } from '../espacial/aPie';
import type { ModeloEspacial } from '../espacial/compilador';
import { celdasDeCirculacion } from '../espacial/tramos';
import { validarEspacio } from '../validacion/espacio';

/** Velocidad a pie si el espacio no la indica (doc. 04 §9). */
export const VELOCIDAD_PEATON_KMH = 4.5;

/** JSON con claves ordenadas: la misma información da siempre el mismo texto. */
export function jsonCanonico(valor: unknown): string {
  if (valor === null || typeof valor !== 'object') return JSON.stringify(valor) ?? 'null';
  if (Array.isArray(valor)) return `[${valor.map(jsonCanonico).join(',')}]`;
  const o = valor as Record<string, unknown>;
  return `{${Object.keys(o)
    .filter((k) => o[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${jsonCanonico(o[k])}`)
    .join(',')}}`;
}

/**
 * Jornadas de calentamiento y de cierre de un horizonte (doc. 04 §8; paso 1.6, avances 4 y 5).
 * Desde 3 jornadas, por defecto, la primera es de calentamiento y la última de cierre; las dos se
 * simulan y no se miden. Siempre queda al menos una jornada medida.
 */
export function jornadasDelHorizonte(c: {
  dias: number;
  calentamiento?: number;
  jornadaDeCierre?: boolean;
}): { calentamiento: number; jornadaDeCierre: boolean } {
  const jornadaDeCierre = c.dias >= 2 && (c.jornadaDeCierre ?? c.dias >= 3);
  const calentamiento = Math.max(
    0,
    Math.min(c.calentamiento ?? (c.dias >= 3 ? 1 : 0), c.dias - 1 - (jornadaDeCierre ? 1 : 0)),
  );
  return { calentamiento, jornadaDeCierre };
}

/** Huella de 64 bits (16 caracteres hexadecimales) de cualquier valor serializable. */
export function huellaDe(valor: unknown): string {
  const texto = jsonCanonico(valor);
  const a = hashTexto(texto, 0x2545f491);
  const b = hashTexto(texto, 0x9e3779b9);
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

export type Preparacion =
  | {
      ok: true;
      config: ConfiguracionDeEjecucion;
      modelo: ModeloEspacial;
      observaciones: Observacion[];
    }
  | { ok: false; observaciones: Observacion[]; modelo: ModeloEspacial };

export function prepararEjecucion(espacio: Espacio, catalogo: Catalogo): Preparacion {
  const v = validarEspacio(espacio, catalogo);
  if (v.bloquea) return { ok: false, observaciones: v.observaciones, modelo: v.modelo };
  const m = espacio.modelo;
  const modelo = v.modelo;

  const estaciones: EstacionCompilada[] = modelo.estaciones.map((s) => ({
    id: s.id,
    tipo: s.tipo,
    capacidad: s.capacidad,
    compatibles: s.compatibles,
    especializada: s.especializada,
    ...(s.tipoBahia ? { tipoBahia: s.tipoBahia } : {}),
    sigueTurno: s.sigueTurno,
    x: s.centro.x,
    z: s.centro.z,
    rotacion: s.rotacion,
    elementoId: s.elementoId,
  }));
  const personas: PersonaCompilada[] = [];
  for (const rol of ROLES) {
    for (let i = 1; i <= (m.dotacion[rol]?.valor ?? 0); i++) {
      personas.push({ id: `${LETRA_ROL[rol]}${String(i).padStart(2, '0')}`, rol });
    }
  }
  // Técnico fijo por elevador (paso 1.6): solo entra en la huella si se usa.
  const tecnicoPorElevador = m.tecnicoPorElevador?.valor === 'fijo' ? 'fijo' : undefined;
  const demanda = {
    modo: m.demanda.modo,
    unidadesPorDia: m.demanda.unidadesPorDia.valor,
    perfilHorario: m.demanda.perfilHorario.valor,
    mezcla: m.demanda.mezcla.valor,
    inicioCitas: horaASegundos(m.jornada.inicioCitas.valor),
    ultimaCita: horaASegundos(m.jornada.ultimaCita.valor),
    agenda:
      m.demanda.modo === 'agenda'
        ? (m.demanda.agenda ?? []).map((c) => ({
            t: instante(c.dia, horaASegundos(c.hora)),
            tipoUnidad: c.tipoUnidad,
            ...(c.atributos ? { atributos: c.atributos } : {}),
          }))
        : [],
  };
  const traslados: ConfiguracionDeEjecucion['traslados'] =
    m.movimiento.modo === 'fijo'
      ? {
          modo: 'fijo',
          duracionS: Math.round(m.movimiento.trasladoFijoMin.valor * SEGUNDOS_POR_MINUTO),
        }
      : {
          modo: 'distancia',
          velocidadMs: m.movimiento.velocidadVehiculoKmh.valor / 3.6,
          maniobraS: m.movimiento.maniobraS.valor,
          distancias: modelo.distancias.map((fila) =>
            fila.map((d) => (d === null ? null : Math.round(d * 100) / 100)),
          ),
          // Los autos se ceden el paso (paso 1.6, avance 3): tramos que reserva cada traslado.
          ...(m.politicas.cederElPaso ? { circulacion: celdasDeCirculacion(modelo) } : {}),
        };
  // Tiempos a pie (paso 1.6, avance 2): solo si el proceso tiene recorridos de personas.
  const usaRecorridos = m.proceso.etapas.some((e) => (e.recorridos?.length ?? 0) > 0);
  let aPie: APieCompilado | undefined;
  if (usaRecorridos) {
    if (m.movimiento.modo === 'fijo') {
      aPie = {
        modo: 'fijo',
        duracionS: Math.round(m.movimiento.trasladoFijoMin.valor * SEGUNDOS_POR_MINUTO),
      };
    } else {
      const d = distanciasAPie(espacio, modelo.estaciones);
      aPie = {
        modo: 'distancia',
        velocidadMs: (m.movimiento.velocidadPeatonKmh?.valor ?? VELOCIDAD_PEATON_KMH) / 3.6,
        puntos: d.puntos.map((x) => x.id),
        distancias: d.distancias.map((fila) => fila.map((x) => Math.round(x * 100) / 100)),
      };
    }
  }
  // Refrigerio de los técnicos (paso 1.2); sin él, no entra en la huella (las anteriores no cambian).
  const r = m.refrigerio;
  const refrigerio =
    r && r.duracionMin.valor > 0
      ? {
          inicio: horaASegundos(r.inicio.valor),
          duracionS: Math.round(r.duracionMin.valor * SEGUNDOS_POR_MINUTO),
          modo: r.modo.valor,
        }
      : null;
  // Jornadas de calentamiento y de cierre (paso 1.6, avances 4 y 5): solo entran en la huella si
  // hay alguna (las configuraciones de un día no cambian).
  const { calentamiento, jornadaDeCierre } = jornadasDelHorizonte(espacio.configuracionAleatoria);
  const parametros = {
    personas,
    turno: { inicio: m.jornada.inicioTurno.valor, fin: m.jornada.finTurno.valor },
    cierre: m.jornada.cierre.valor,
    atencion: { inicio: m.atencion.inicio.valor, fin: m.atencion.fin.valor },
    redondeo: m.redondeoPromesaMin.valor,
    traslados: {
      modo: m.movimiento.modo,
      v: m.movimiento.velocidadVehiculoKmh.valor,
      man: m.movimiento.maniobraS.valor,
      fijo: m.movimiento.trasladoFijoMin.valor,
    },
    politicas: m.politicas,
    dias: espacio.configuracionAleatoria.dias,
    refrigerio: refrigerio ?? undefined,
    tecnicoPorElevador,
    ...(calentamiento > 0 ? { calentamiento } : {}),
    ...(jornadaDeCierre ? { jornadaDeCierre } : {}),
  };
  // El origen (supuesto/ingresado) y el supuesto guardado para «volver al supuesto» no alteran
  // resultados: no entran en las huellas.
  const sinOrigen = (x: unknown) =>
    JSON.parse(
      JSON.stringify(x, (k, val) =>
        k === 'origen' || k === 'nota' || k === 'supuesto' ? undefined : val,
      ),
    ) as unknown;
  const huellas = {
    layout: huellaDe({
      estaciones,
      distancias: traslados.modo === 'distancia' ? traslados.distancias : null,
      aPie,
      colaIngreso: modelo.colaIngreso?.capacidad ?? null,
    }),
    proceso: huellaDe(sinOrigen(m.proceso)),
    parametros: huellaDe(parametros),
    demanda: huellaDe(demanda),
    total: '',
  };
  huellas.total = huellaDe({
    ...huellas,
    semilla: espacio.configuracionAleatoria.semilla,
    versionMotor: VERSION_MOTOR,
  });

  const config: ConfiguracionDeEjecucion = {
    versionMotor: VERSION_MOTOR,
    ejecucionId: `ej-${huellas.total}`,
    huellas,
    proceso: m.proceso,
    estaciones,
    personas,
    turno: {
      inicio: horaASegundos(m.jornada.inicioTurno.valor),
      fin: horaASegundos(m.jornada.finTurno.valor),
    },
    cierre: horaASegundos(m.jornada.cierre.valor),
    atencion: {
      inicio: horaASegundos(m.atencion.inicio.valor),
      fin: horaASegundos(m.atencion.fin.valor),
    },
    redondeoPromesaS: Math.round(m.redondeoPromesaMin.valor * SEGUNDOS_POR_MINUTO),
    dias: espacio.configuracionAleatoria.dias,
    demanda,
    traslados,
    ...(aPie ? { aPie } : {}),
    ...(modelo.colaIngreso ? { colaIngreso: { capacidad: modelo.colaIngreso.capacidad } } : {}),
    ...(calentamiento > 0 ? { calentamiento } : {}),
    ...(jornadaDeCierre ? { jornadaDeCierre: true as const } : {}),
    politicas: m.politicas,
    refrigerio,
    ...(tecnicoPorElevador ? { tecnicoPorElevador } : {}),
    semilla: espacio.configuracionAleatoria.semilla,
    replicas: espacio.configuracionAleatoria.replicasAutomaticas
      ? REPLICAS_INICIALES
      : espacio.configuracionAleatoria.replicas,
    replicasAutomaticas: espacio.configuracionAleatoria.replicasAutomaticas,
  };
  return { ok: true, config, modelo, observaciones: v.observaciones };
}
